// src/components/PlayerList.tsx - Updated with deck selection

import React, { useState } from 'react';
import { TSOProfile, DeckChoice } from '../types';
import DeckSelector from './DeckSelector';

interface PlayerListProps {
  players: TSOProfile[];
  currentUserFaction: number;
  onChallenge: (playerId: string, deckChoice: DeckChoice) => void;
}

const PlayerList: React.FC<PlayerListProps> = ({ players, currentUserFaction, onChallenge }) => {
  const [filter, setFilter] = useState<'all' | 'same' | 'opposite'>('all');
  const [challengingPlayer, setChallengingPlayer] = useState<string | null>(null);
  const [showDeckSelector, setShowDeckSelector] = useState<string | null>(null);
  const [selectedDeck, setSelectedDeck] = useState<DeckChoice>('deck_1');

  const filteredPlayers = players.filter(player => {
    switch (filter) {
      case 'same':
        return player.faction_id === currentUserFaction;
      case 'opposite':
        return player.faction_id !== currentUserFaction;
      default:
        return true;
    }
  });

  const handleChallengeClick = (playerId: string) => {
    setShowDeckSelector(playerId);
    setSelectedDeck('deck_1'); // Reset to default
  };

  const handleDeckSelect = (deckChoice: DeckChoice) => {
    setSelectedDeck(deckChoice);
  };

  const handleConfirmChallenge = async (playerId: string) => {
    setChallengingPlayer(playerId);
    setShowDeckSelector(null);
    
    try {
      await onChallenge(playerId, selectedDeck);
    } finally {
      setChallengingPlayer(null);
    }
  };

  const handleCancelChallenge = () => {
    setShowDeckSelector(null);
    setSelectedDeck('deck_1');
  };

  const getActivityStatus = (lastActivity: string) => {
    const diffDays = Math.floor((Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return { text: 'Active today', color: 'text-green-400' };
    if (diffDays === 1) return { text: 'Active yesterday', color: 'text-yellow-400' };
    if (diffDays <= 7) return { text: `Active ${diffDays}d ago`, color: 'text-gray-400' };
    return { text: 'Inactive', color: 'text-red-400' };
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Find Opponents</h2>
        
        {/* Filter buttons */}
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            All Players ({players.length})
          </button>
          <button
            onClick={() => setFilter('opposite')}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              filter === 'opposite' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {currentUserFaction === 1 ? 'Attackers' : 'Defenders'} 
            ({players.filter(p => p.faction_id !== currentUserFaction).length})
          </button>
          <button
            onClick={() => setFilter('same')}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              filter === 'same' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {currentUserFaction === 1 ? 'Defenders' : 'Attackers'} 
            ({players.filter(p => p.faction_id === currentUserFaction).length})
          </button>
        </div>
      </div>

      {/* Deck Selection Modal */}
      {showDeckSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">
                Challenge {players.find(p => p.user_id === showDeckSelector)?.username}
              </h3>
              <button
                onClick={handleCancelChallenge}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <DeckSelector
              onDeckSelect={handleDeckSelect}
              selectedDeck={selectedDeck}
              title="Choose your deck for this challenge"
            />

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => handleConfirmChallenge(showDeckSelector)}
                disabled={challengingPlayer === showDeckSelector}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded font-medium transition-colors"
              >
                {challengingPlayer === showDeckSelector ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending Challenge...
                  </span>
                ) : (
                  'Send Challenge'
                )}
              </button>
              <button
                onClick={handleCancelChallenge}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredPlayers.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-1H21a2 2 0 01-2 2v1" />
            </svg>
          </div>
          <h3 className="text-xl font-medium text-white mb-2">No players found</h3>
          <p className="text-gray-400">Try adjusting your filter or check back later</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPlayers.map(player => {
            const activity = getActivityStatus(player.last_activity_date);
            const isOpposingFaction = player.faction_id !== currentUserFaction;
            
            return (
              <div 
                key={player.user_id}
                className="bg-gray-800 rounded-lg border border-gray-700 p-4 hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-white flex items-center">
                      {player.username}
                      {player.is_admin && (
                        <span className="ml-2 bg-yellow-600 text-black text-xs px-2 py-1 rounded">
                          ADMIN
                        </span>
                      )}
                    </h3>
                    <p className={`text-sm ${
                      player.faction_id === 1 ? 'text-blue-400' : 'text-red-400'
                    }`}>
                      {player.faction_id === 1 ? 'Defender' : 'Attacker'}
                      {isOpposingFaction && (
                        <span className="ml-2 bg-orange-600 text-white text-xs px-2 py-1 rounded">
                          RIVAL FACTION
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Streak:</span>
                    <span className="text-white">{player.consecutive_days_played} days</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span className={activity.color}>{activity.text}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleChallengeClick(player.user_id)}
                  disabled={challengingPlayer === player.user_id || showDeckSelector !== null}
                  className={`w-full mt-4 px-4 py-2 rounded font-medium text-sm transition-colors ${
                    isOpposingFaction
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {challengingPlayer === player.user_id ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Challenging...
                    </span>
                  ) : (
                    `Challenge ${player.username}`
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayerList;